'use client';
import 'client-only';
import Link from 'next/link';
import { signOut, useSession } from 'next-auth/react';
import { useEffect, useRef, useState } from 'react';
import useSWR from 'swr';

interface StreakResp {
  streak: number;
}

const streakFetcher = async (
  url: string,
  userId: string,
): Promise<StreakResp> => {
  const res = await fetch(url, {
    headers: { 'X-User-Id': userId },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as StreakResp;
};

/* Coral signed-in user pill — denim-deep capsule with screenname + flame
   streak + dropdown. Mirrors the IA from the legacy UserStatus
   (app/components/signedin.tsx) but in coral skin. */
export function UserPill() {
  const { data: session } = useSession();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const userId = session?.userId;
  // Same-origin proxy (/api/spot/[...path]) → MO_GAME service binding →
  // mtrobs-game. mtrobs-game has no public route, so the browser must
  // never talk to it directly — that's also why the legacy
  // mo-game.dng.workers.dev was triggering CORS errors here.
  const { data: streakData } = useSWR(
    userId ? ['/api/spot/me/streak', userId] : null,
    ([url, uid]) => streakFetcher(url, uid as string),
    { revalidateOnFocus: false, refreshInterval: 1000 * 60 * 5 },
  );

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!session) return null;
  const displayName = session.screenname;
  const isDefault = session.screennameIsDefault;
  const streak = streakData?.streak ?? 0;

  return (
    <div ref={containerRef} className="mo-user-pill-wrap">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mo-user-pill"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={
          isDefault
            ? 'Account menu — auto-generated screenname, click to customize'
            : 'Account menu'
        }
      >
        <span
          className={`mo-user-pill-name ${isDefault ? 'is-default' : ''}`}
        >
          {displayName}
        </span>
        {streak > 0 && (
          <span className="mo-user-pill-streak" aria-label={`${streak}-day streak`}>
            <span aria-hidden>🔥</span>
            <span>{streak}</span>
          </span>
        )}
        <span className="mo-user-pill-caret" aria-hidden>
          ▾
        </span>
        {isDefault && (
          <span
            className="mo-user-pill-dot"
            aria-hidden
            title="auto-generated screenname"
          />
        )}
      </button>

      {open && (
        <div role="menu" className="mo-user-pill-menu">
          <div className="mo-user-pill-menu-header">
            <div className={`name ${isDefault ? 'is-default' : ''}`}>
              {displayName}
            </div>
            {isDefault && <span className="auto-tag">auto</span>}
          </div>
          {isDefault && (
            <Link
              href="/settings/screenname?callbackUrl=/"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="mo-user-pill-menu-item is-amber"
            >
              Pick your screenname
            </Link>
          )}
          <Link
            href="/user"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="mo-user-pill-menu-item"
          >
            Profile
          </Link>
          <Link
            href="/account/alerts"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="mo-user-pill-menu-item"
          >
            Alerts
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              signOut({ callbackUrl: '/' });
            }}
            className="mo-user-pill-menu-item is-signout"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
